import { onBeforeUnmount, ref, type Ref } from "vue";
import { createTranscription } from "shared/api/transcription.api";

export type VoiceInputErrorKey = "microphoneDenied" | "transcriptionFailed";

export interface UseVoiceInputReturn {
  isSupported: boolean;
  isRecording: Ref<boolean>;
  isTranscribing: Ref<boolean>;
  error: Ref<VoiceInputErrorKey | null>;
  toggle: () => Promise<void>;
}

const FALLBACK_MIME_TYPE = "audio/webm";

/**
 * There is one microphone, but a composable per field — and the browser will
 * happily open a second stream while the first is still running, leaving a
 * recording nobody is watching, a second indicator, and two paid
 * transcriptions. The field that starts takes the microphone off the one
 * before it.
 */
let activeRecording: (() => void) | null = null;

/**
 * Counts hand-overs, so a start that is still waiting on the permission prompt
 * can tell that another field has claimed the microphone since.
 */
let handOverSequence = 0;

/**
 * MediaRecorder is absent in older Safari, and `navigator.mediaDevices` is
 * undefined on any insecure origin — so this is a real runtime check, not a
 * defensive one. Callers hide the control entirely when it returns false.
 */
const detectSupport = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  if (typeof window.MediaRecorder === "undefined") {
    return false;
  }

  return navigator.mediaDevices?.getUserMedia !== undefined;
};

/**
 * Record from the microphone and hand the transcribed text to `onTranscript`.
 */
export const useVoiceInput = (
  onTranscript: (text: string) => void,
): UseVoiceInputReturn => {
  const isRecording = ref(false);
  const isTranscribing = ref(false);
  const error = ref<VoiceInputErrorKey | null>(null);

  let recorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let chunks: Blob[] = [];
  let mimeType = FALLBACK_MIME_TYPE;
  let isStarting = false;
  let isStopping = false;
  let isUnmounted = false;

  const releaseStream = (): void => {
    stream?.getTracks().forEach((track) => track.stop());
    stream = null;
    recorder = null;
  };

  const transcribe = async (): Promise<void> => {
    // Unmounting stops the recorder, which fires onstop and lands here. Without
    // this the audio is still uploaded and the paid transcription still runs,
    // only for the result to be emitted into a component that no longer exists.
    // Clicking away to another student mid-recording is the common case.
    if (isUnmounted === true) {
      chunks = [];
      return;
    }

    if (chunks.length === 0) {
      // A muted or absent input device produces no chunks at all. Staying
      // silent here leaves no text, no spinner and no toast — identical to a
      // button that simply does nothing.
      error.value = "transcriptionFailed";
      isStopping = false;
      return;
    }

    isTranscribing.value = true;

    try {
      const text = await createTranscription(
        new Blob(chunks, { type: mimeType }),
      );
      const trimmed = text.trim();

      if (trimmed.length > 0) {
        onTranscript(trimmed);
      }
    } catch {
      error.value = "transcriptionFailed";
    } finally {
      isTranscribing.value = false;
      isStopping = false;
      chunks = [];
    }
  };

  const start = async (): Promise<void> => {
    // `isRecording` only flips once getUserMedia resolves, and the first-use
    // permission prompt keeps that pending for seconds. Without a guard set
    // synchronously here, a second click in that window opens a second stream
    // and orphans the first one — the microphone stays live for the session.
    if (isStarting === true) {
      return;
    }

    // Before the microphone is opened, so the previous field has released it.
    activeRecording?.();

    const startedAt = ++handOverSequence;

    isStarting = true;
    error.value = null;
    chunks = [];

    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Covers both an outright block and the teacher dismissing the prompt.
      error.value = "microphoneDenied";
      return;
    } finally {
      isStarting = false;
    }

    // The prompt can stay open for seconds; another field may have claimed the
    // microphone in the meantime. Recording anyway would give two live streams,
    // which is the very thing the hand-over exists to prevent.
    if (startedAt !== handOverSequence) {
      releaseStream();
      return;
    }

    recorder = new MediaRecorder(stream);
    mimeType =
      recorder.mimeType.length > 0 ? recorder.mimeType : FALLBACK_MIME_TYPE;

    recorder.ondataavailable = (event: BlobEvent): void => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    recorder.onstop = (): void => {
      releaseStream();
      void transcribe();
    };

    recorder.start();
    isRecording.value = true;
    activeRecording = stop;
  };

  const stop = (): void => {
    isRecording.value = false;

    if (activeRecording === stop) {
      activeRecording = null;
    }

    if (recorder === null) {
      return;
    }

    // `onstop` fires asynchronously, and `isTranscribing` only flips once it
    // runs. Without this flag the button renders as an enabled idle microphone
    // in that gap: a click there would start a fresh stream, and the previous
    // recorder's `onstop` would then release *that* stream's tracks, leaving
    // the UI showing "recording" while nothing is captured.
    isStopping = true;

    // Transcription is kicked off from `onstop`, once the last chunk landed.
    recorder.stop();
  };

  const toggle = async (): Promise<void> => {
    if (isStopping === true) {
      return;
    }

    if (isRecording.value === true) {
      stop();
      return;
    }

    await start();
  };

  onBeforeUnmount(() => {
    // Set before stop(): stop() triggers onstop, which calls transcribe().
    isUnmounted = true;

    // Navigating away mid-recording must still drop the microphone, otherwise
    // the browser keeps showing its recording indicator for the whole session.
    if (isRecording.value === true) {
      stop();
      return;
    }

    releaseStream();
  });

  return {
    isSupported: detectSupport(),
    isRecording,
    isTranscribing,
    error,
    toggle,
  };
};
